import { BACK_TYPE, PRESCRIPTION_FULFILLMENT_OPTION, STEP_TYPE } from '@abiglobalhealth/go-types'
import { ReactNode, useEffect, useState } from 'react'

import { Button } from '@/components/Button'
import { usePrescriptionFulfillmentOptions } from '@/hooks/usePrescriptionFulfillmentOptions'
import { useStep } from '@/hooks/useStep'
import { getSlugTranslation } from '@/locale/utils'
import { cn } from '@/utils/tailwind'

import { DeliveryForm } from './DeliveryForm'
import { CollectIcon } from './icons/CollectIcon'
import { DeliveryIcon } from './icons/DeliveryIcon'
import DownloadPdfIcon from './icons/DownloadPdfIcon'
import { PharmacySelector } from './PharmacySelector'
import { UserAddressForm } from './UserAddressForm'

interface OptionProps {
  icon: ReactNode
  title: string
  description: string
}

interface CardProps extends OptionProps {
  onClick: () => void
  selected?: boolean
}

const Card = ({ icon, title, description, onClick, selected }: CardProps) => (
  <button
    className={cn(
      'flex w-full flex-col gap-4 rounded-md border bg-white p-5 shadow-sm',
      selected ? 'border-primary bg-primary-light' : 'border-transparent'
    )}
    onClick={onClick}
  >
    <div className="flex w-full gap-3">
      {icon}
      <h2 className="font-bold">{getSlugTranslation(title)}</h2>
    </div>
    <p className="text-gray-text text-left text-sm">{getSlugTranslation(description)}</p>
  </button>
)

const OPTIONS: Record<PRESCRIPTION_FULFILLMENT_OPTION, OptionProps | undefined> = {
  [PRESCRIPTION_FULFILLMENT_OPTION.COLLECT]: {
    icon: <CollectIcon />,
    title: 'fulfillment.collect.title',
    description: 'fulfillment.collect.description'
  },
  [PRESCRIPTION_FULFILLMENT_OPTION.DELIVERY]: {
    icon: <DeliveryIcon />,
    title: 'fulfillment.delivery.title',
    description: 'fulfillment.delivery.description'
  },
  [PRESCRIPTION_FULFILLMENT_OPTION.PDF]: {
    icon: <DownloadPdfIcon />,
    title: 'fulfillment.download.title',
    description: 'fulfillment.download.description'
  },
  [PRESCRIPTION_FULFILLMENT_OPTION.USER_ADDRESS]: undefined
}

export const FulfillmentChoiceSelection = () => {
  const { prescriptionFulfillmentOptions, initialFulfillmentOption } = usePrescriptionFulfillmentOptions()

  const { currentStep, updateFulfillmentStep } = useStep()
  const [selectedOption, setSelectedOption] = useState<PRESCRIPTION_FULFILLMENT_OPTION | null>(null)
  const [currentOption, setCurrentOption] = useState<PRESCRIPTION_FULFILLMENT_OPTION | null>(initialFulfillmentOption)
  const [requiredError, setRequiredError] = useState(false)

  const handleFulfillmentOptionSelection = () => {
    switch (selectedOption) {
      case PRESCRIPTION_FULFILLMENT_OPTION.COLLECT:
      case PRESCRIPTION_FULFILLMENT_OPTION.DELIVERY:
        setCurrentOption(selectedOption)
        break

      case PRESCRIPTION_FULFILLMENT_OPTION.PDF:
        // TODO: Download PDF
        break
    }
  }

  useEffect(() => {
    updateFulfillmentStep({
      option: currentOption,
      backButton: selectedOption ? BACK_TYPE.SHORT : undefined
    })
  }, [currentOption])

  useEffect(() => {
    if (currentStep?.type !== STEP_TYPE.FULFILLMENT_CHOICE_SELECTION || currentStep?.option) return
    setCurrentOption(initialFulfillmentOption)
  }, [currentStep])

  if (currentStep?.type !== STEP_TYPE.FULFILLMENT_CHOICE_SELECTION) return

  if (currentStep?.option === PRESCRIPTION_FULFILLMENT_OPTION.COLLECT) return <PharmacySelector />

  if (currentStep?.option === PRESCRIPTION_FULFILLMENT_OPTION.DELIVERY) return <DeliveryForm />

  if (currentStep?.option === PRESCRIPTION_FULFILLMENT_OPTION.USER_ADDRESS) return <UserAddressForm />

  return (
    <div className="flex flex-1 flex-col gap-2 px-4 py-6">
      <p>{getSlugTranslation('fulfillment.title')}</p>

      <div className="flex w-full flex-col gap-2 py-4">
        {prescriptionFulfillmentOptions?.map((option) => {
          const optionObject = OPTIONS[option]
          if (!optionObject) return null

          const { icon, title, description } = optionObject
          return (
            <Card
              key={option}
              icon={icon}
              title={title}
              description={description}
              onClick={() => {
                setRequiredError(false)
                setSelectedOption((prev) => (prev === option ? null : option))
              }}
              selected={selectedOption === option}
            />
          )
        })}
      </div>

      <Button
        customClassName="shadow-sm"
        onClick={() => {
          if (!selectedOption) return setRequiredError(true)
          handleFulfillmentOptionSelection()
        }}
      >
        {getSlugTranslation('button.confirm')}
      </Button>

      {requiredError && <p className="w-full text-sm text-red-500">{getSlugTranslation('fulfillment.please_select')}</p>}
    </div>
  )
}
